import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import TrialBanner from '../components/TrialBanner';
import PullToRefresh from '../components/PullToRefresh';
import {
  Scale,
  Briefcase,
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Users,
  MessageSquare,
  FolderOpen,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  LayoutGrid,
  List,
  Calendar,
  FileCode,
  BarChart3,
  UserPlus
} from 'lucide-react';
import DocumentTemplatesSection from '../components/DocumentTemplatesSection';
import FirmLetterheadManager from '../components/FirmLetterheadManager';
import CaseReportsDashboard from '../components/CaseReportsDashboard';
import TeamManagement from '../components/TeamManagement';
import DataImportTool from '../components/DataImportTool';
import EventCategoriesManager from '../components/EventCategoriesManager';
import TaskTemplatesManager from '../components/TaskTemplatesManager';
import WorkflowAutomationsManager from '../components/WorkflowAutomationsManager';
import EmailIntegration from '../components/EmailIntegration';
import SMSSettingsManager from '../components/SMSSettingsManager';
import BillingAIPage from './BillingAIPage';
import IntakeAppPage from './IntakeAppPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Case status colors
const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  on_hold: 'bg-orange-100 text-orange-700',
  closed: 'bg-slate-100 text-slate-700',
  archived: 'bg-red-100 text-red-700'
};

// Stage colors
const stageColors = {
  intake: '#3b82f6',
  investigation: '#8b5cf6',
  demand: '#f59e0b',
  negotiation: '#10b981',
  filed: '#ef4444',
  discovery: '#6366f1',
  motions: '#ec4899',
  mediation: '#14b8a6',
  trial_prep: '#f97316',
  trial: '#dc2626',
  post_trial: '#7c3aed',
  appeal: '#0891b2',
  settled: '#22c55e',
  closed: '#64748b'
};

export default function CaseManagementPage() {
  const navigate = useNavigate();
  const { token } = useAuth();

  // Read tab from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = searchParams.get('tab') || 'dashboard';

  // State
  const [cases, setCases] = useState([]);
  const [stages, setStages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStage, setFilterStage] = useState('all');

  // Create case dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCase, setNewCase] = useState({
    name: '',
    case_type: '',
    client_id: '',
    client_name: '',
    description: '',
    fee_type: 'hourly',
    hourly_rate: '',
    template_id: ''
  });
  const [creating, setCreating] = useState(false);
  const [contacts, setContacts] = useState([]);

  // Matter Template dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    case_type: '',
    description: '',
    default_fee_type: 'hourly',
    default_hourly_rate: '',
    custom_fields: []
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchDashboard();
    fetchCases();
    fetchStages();
    fetchTemplates();
    fetchContacts();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchCases = async () => {
    try {
      let url = `${API_URL}/api/case-management/cases`;
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterStage !== 'all') params.append('stage', filterStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCases(data);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/stages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStages(data.default_stages || []);
      }
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      // Try to get contacts from intake leads
      const res = await fetch(`${API_URL}/api/intake/leads?pipeline_stage=signed_converted`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.map(lead => ({
          id: lead.contact_id || lead.id,
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          from_intake: true,
          lead_id: lead.id
        })));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchCases();
    }
  }, [filterStatus, filterStage, searchQuery]);

  const createCase = async () => {
    if (!newCase.name || !newCase.case_type) {
      toast.error('Please provide a case name and type');
      return;
    }

    setCreating(true);
    try {
      // If client selected from contacts with lead_id, use Intake integration
      const selectedContact = contacts.find(c => c.id === newCase.client_id);
      
      let endpoint = `${API_URL}/api/case-management/cases`;
      let payload = {
        name: newCase.name,
        case_type: newCase.case_type,
        client_id: newCase.client_id || 'new-client',
        description: newCase.description,
        fee_type: newCase.fee_type,
        hourly_rate: newCase.hourly_rate ? parseFloat(newCase.hourly_rate) : null,
        template_id: newCase.template_id || null
      };

      // Use intake integration if available
      if (selectedContact?.from_intake && selectedContact?.lead_id) {
        endpoint = `${API_URL}/api/case-management/from-intake`;
        payload = {
          lead_id: selectedContact.lead_id,
          template_id: newCase.template_id || null,
          assigned_attorneys: [],
          transfer_documents: true
        };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const caseData = await res.json();
        toast.success('Case created successfully!');
        setShowCreateDialog(false);
        setNewCase({
          name: '',
          case_type: '',
          client_id: '',
          client_name: '',
          description: '',
          fee_type: 'hourly',
          hourly_rate: '',
          template_id: ''
        });
        fetchCases();
        fetchDashboard();
        navigate(`/case-management/${caseData.id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create case');
      }
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Failed to create case');
    } finally {
      setCreating(false);
    }
  };

  // Template handlers
  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      case_type: '',
      description: '',
      default_fee_type: 'hourly',
      default_hourly_rate: '',
      custom_fields: []
    });
    setEditingTemplate(null);
  };

  const handleCreateTemplate = () => {
    resetTemplateForm();
    setShowTemplateDialog(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '',
      case_type: template.case_type || '',
      description: template.description || '',
      default_fee_type: template.default_fee_type || 'hourly',
      default_hourly_rate: template.default_hourly_rate || '',
      custom_fields: template.custom_fields || []
    });
    setShowTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.case_type) {
      toast.error('Please provide a template name and case type');
      return;
    }

    setSavingTemplate(true);
    try {
      const url = editingTemplate 
        ? `${API_URL}/api/case-management/templates/${editingTemplate.id}`
        : `${API_URL}/api/case-management/templates`;
      
      // Prepare data, removing empty optional fields
      const data = {
        name: templateForm.name,
        case_type: templateForm.case_type,
        description: templateForm.description || null,
        default_fee_type: templateForm.default_fee_type,
        default_hourly_rate: templateForm.default_hourly_rate ? parseFloat(templateForm.default_hourly_rate) : null,
        custom_fields: templateForm.custom_fields
      };

      const res = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setShowTemplateDialog(false);
        fetchTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const getStageLabel = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.label || stageId;
  };

  const getStageColor = (stageId) => {
    return stageColors[stageId] || '#64748b';
  };

  // Refresh handler for pull-to-refresh - must be before any returns
  const handleRefresh = useCallback(async () => {
    await fetchCases();
    toast.success('Data refreshed');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-navy mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Legal AIDynamics...</p>
        </div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div data-testid="case-management-page">
        {/* Trial Banner */}
        <TrialBanner />
        
        {/* Header */}
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-4">
                <Link to="/hub" className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                  <img src="/logo.png" alt="Legal AIDynamics" className="w-8 h-8 sm:w-9 sm:h-9 rounded-md object-contain" />
                </Link>
                <div className="h-6 w-px bg-border hidden sm:block" />
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-navy flex items-center justify-center">
                    <Briefcase className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="font-heading text-base sm:text-xl font-bold text-navy">Legal AIDynamics</h1>
                    <p className="text-xs text-muted-foreground hidden sm:block">Your legal suite</p>
                  </div>
                </div>
              </div>
              
              <Button size="sm" className="sm:size-default bg-navy hover:bg-navy-light" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Case</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
              <TabsList className="inline-flex w-max sm:w-auto">
                <TabsTrigger value="dashboard" className="text-xs sm:text-sm">Dashboard</TabsTrigger>
                <TabsTrigger value="cases" className="text-xs sm:text-sm">Cases</TabsTrigger>
                <TabsTrigger value="intake" className="text-xs sm:text-sm" data-testid="intake-tab">
                  <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Intake</span>
                </TabsTrigger>
                <TabsTrigger value="billing-ai" className="text-xs sm:text-sm" data-testid="billing-ai-tab">
                  <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Billing</span>
                </TabsTrigger>
                <TabsTrigger value="reports" className="text-xs sm:text-sm" data-testid="reports-tab">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Reports</span>
              </TabsTrigger>
              <TabsTrigger value="templates" className="text-xs sm:text-sm">Templates</TabsTrigger>
              <TabsTrigger value="documents" className="text-xs sm:text-sm">Documents</TabsTrigger>
              <TabsTrigger value="firm-settings" className="text-xs sm:text-sm">Settings</TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Active Cases</p>
                        <p className="text-3xl font-bold text-navy">{dashboard?.cases?.active || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Pending Cases</p>
                        <p className="text-3xl font-bold text-amber-600">{dashboard?.cases?.pending || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Billed</p>
                        <p className="text-3xl font-bold text-blue-600">
                          ${(dashboard?.billing?.total_billed || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Outstanding</p>
                        <p className="text-3xl font-bold text-red-600">
                          ${(dashboard?.billing?.outstanding || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cases by Stage */}
              {dashboard?.by_stage && Object.keys(dashboard.by_stage).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Cases by Stage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(dashboard.by_stage).map(([stage, count]) => (
                        <div 
                          key={stage}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50"
                        >
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getStageColor(stage) }}
                          />
                          <span className="text-sm font-medium">{getStageLabel(stage)}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Cases */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Recent Cases</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('cases')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_cases?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_cases.map((caseItem) => (
                        <Link 
                          key={caseItem.id}
                          to={`/case-management/${caseItem.id}`}
                          className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div 
                              className="w-2 h-10 rounded-full"
                              style={{ backgroundColor: getStageColor(caseItem.current_stage) }}
                            />
                            <div>
                              <h4 className="font-medium">{caseItem.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {caseItem.case_number} • {caseItem.client_name}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={statusColors[caseItem.status]}>
                              {caseItem.status}
                            </Badge>
                            <Badge variant="outline">
                              {getStageLabel(caseItem.current_stage)}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No cases yet</p>
                      <Button 
                        className="mt-4" 
                        size="sm"
                        onClick={() => setShowCreateDialog(true)}
                      >
                        Create Your First Case
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Intake Tab */}
          <TabsContent value="intake">
            <IntakeAppPage embedded={true} />
          </TabsContent>

          {/* Billing.AI Tab */}
          <TabsContent value="billing-ai">
            <BillingAIPage embedded={true} />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <CaseReportsDashboard />
          </TabsContent>
          {/* All Cases Tab */}
          <TabsContent value="cases">
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search cases..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterStage} onValueChange={setFilterStage}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stages</SelectItem>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Cases List/Grid */}
              {cases.length > 0 ? (
                viewMode === 'grid' ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cases.map((caseItem) => (
                      <Link key={caseItem.id} to={`/case-management/${caseItem.id}`}>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-base">{caseItem.name}</CardTitle>
                                <CardDescription>{caseItem.case_number}</CardDescription>
                              </div>
                              <Badge className={statusColors[caseItem.status]}>
                                {caseItem.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm">
                                <Users className="w-4 h-4 text-muted-foreground" />
                                <span>{caseItem.client_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: getStageColor(caseItem.current_stage) }}
                                />
                                <span className="text-sm">{getStageLabel(caseItem.current_stage)}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                                <span>{caseItem.document_count || 0} docs</span>
                                <span>${(caseItem.total_billed || 0).toLocaleString()} billed</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {cases.map((caseItem) => (
                          <Link 
                            key={caseItem.id}
                            to={`/case-management/${caseItem.id}`}
                            className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div 
                                className="w-2 h-12 rounded-full"
                                style={{ backgroundColor: getStageColor(caseItem.current_stage) }}
                              />
                              <div>
                                <h4 className="font-medium">{caseItem.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {caseItem.case_number} • {caseItem.client_name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <Badge className={statusColors[caseItem.status]}>
                                {caseItem.status}
                              </Badge>
                              <Badge variant="outline">
                                {getStageLabel(caseItem.current_stage)}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                ${(caseItem.total_billed || 0).toLocaleString()}
                              </span>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Briefcase className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Cases Found</h3>
                    <p className="text-muted-foreground mb-6">
                      {searchQuery || filterStatus !== 'all' || filterStage !== 'all' 
                        ? 'Try adjusting your filters'
                        : 'Get started by creating your first case'}
                    </p>
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Case
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Matter Templates Tab */}
          <TabsContent value="templates">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Matter Templates</h2>
                  <p className="text-sm text-muted-foreground">
                    Create templates with custom fields for different case types
                  </p>
                </div>
                <Button onClick={handleCreateTemplate} data-testid="new-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </div>

              {templates.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
                      <CardHeader>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <CardDescription className="capitalize">{template.case_type?.replace(/_/g, ' ')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Custom Fields</span>
                            <span>{template.custom_fields?.length || 0}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Default Fee</span>
                            <span className="capitalize">{template.default_fee_type?.replace(/_/g, ' ') || 'Not set'}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button variant="outline" className="flex-1" size="sm" onClick={() => handleEditTemplate(template)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteTemplate(template.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Templates Yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Create templates with custom fields for different case types
                    </p>
                    <Button onClick={handleCreateTemplate}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Document Templates Tab */}
          <TabsContent value="documents">
            <DocumentTemplatesSection />
          </TabsContent>

          {/* Firm Settings Tab */}
          <TabsContent value="firm-settings" className="space-y-6">
            <Tabs defaultValue="team" className="w-full">
              <TabsList className="grid w-full grid-cols-8 max-w-5xl">
                <TabsTrigger value="team" data-testid="firm-team-tab">Team</TabsTrigger>
                <TabsTrigger value="task-templates" data-testid="firm-templates-tab">Templates</TabsTrigger>
                <TabsTrigger value="automations" data-testid="firm-automations-tab">Automations</TabsTrigger>
                <TabsTrigger value="event-categories" data-testid="firm-events-tab">Events</TabsTrigger>
                <TabsTrigger value="email" data-testid="firm-email-tab">Email</TabsTrigger>
                <TabsTrigger value="sms" data-testid="firm-sms-tab">SMS</TabsTrigger>
                <TabsTrigger value="letterhead" data-testid="firm-letterhead-tab">Letterhead</TabsTrigger>
                <TabsTrigger value="data-import" data-testid="firm-import-tab">Import</TabsTrigger>
              </TabsList>
              <TabsContent value="team" className="mt-6">
                <TeamManagement />
              </TabsContent>
              <TabsContent value="task-templates" className="mt-6">
                <TaskTemplatesManager />
              </TabsContent>
              <TabsContent value="automations" className="mt-6">
                <WorkflowAutomationsManager />
              </TabsContent>
              <TabsContent value="event-categories" className="mt-6">
                <EventCategoriesManager />
              </TabsContent>
              <TabsContent value="email" className="mt-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold">Email Integration</h3>
                    <p className="text-sm text-gray-500">Connect your Outlook or Gmail to file emails to cases and track billable time</p>
                  </div>
                  <EmailIntegration />
                </div>
              </TabsContent>
              <TabsContent value="sms" className="mt-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold">SMS Messaging</h3>
                    <p className="text-sm text-gray-500">Configure Twilio SMS integration and manage message templates</p>
                  </div>
                  <SMSSettingsManager />
                </div>
              </TabsContent>
              <TabsContent value="letterhead" className="mt-6">
                <FirmLetterheadManager />
              </TabsContent>
              <TabsContent value="data-import" className="mt-6">
                <DataImportTool />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Case Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Create New Case
            </DialogTitle>
            <DialogDescription>
              Create a new case/matter to start tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="case-name">Case Name *</Label>
              <Input
                id="case-name"
                placeholder="e.g., Smith v. Jones Auto Accident"
                value={newCase.name}
                onChange={(e) => setNewCase({ ...newCase, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="case-type">Case Type *</Label>
              <Select
                value={newCase.case_type}
                onValueChange={(value) => setNewCase({ ...newCase, case_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select case type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal_injury">Personal Injury</SelectItem>
                  <SelectItem value="family_law">Family Law</SelectItem>
                  <SelectItem value="criminal_defense">Criminal Defense</SelectItem>
                  <SelectItem value="business_litigation">Business Litigation</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="estate_planning">Estate Planning</SelectItem>
                  <SelectItem value="employment">Employment</SelectItem>
                  <SelectItem value="immigration">Immigration</SelectItem>
                  <SelectItem value="bankruptcy">Bankruptcy</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {contacts.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="client">Client (from Intake.AI)</Label>
                <Select
                  value={newCase.client_id}
                  onValueChange={(value) => {
                    const contact = contacts.find(c => c.id === value);
                    setNewCase({ 
                      ...newCase, 
                      client_id: value,
                      client_name: contact?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client or enter new" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">+ Add New Client</SelectItem>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} {contact.from_intake && '(Intake.AI)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newCase.client_id && contacts.find(c => c.id === newCase.client_id)?.from_intake && (
                  <p className="text-xs text-emerald-600">
                    Documents from Intake.AI will be automatically transferred
                  </p>
                )}
              </div>
            )}

            {templates.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="template">Matter Template (optional)</Label>
                <Select
                  value={newCase.template_id}
                  onValueChange={(value) => setNewCase({ ...newCase, template_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Template</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fee-type">Fee Type</Label>
                <Select
                  value={newCase.fee_type}
                  onValueChange={(value) => setNewCase({ ...newCase, fee_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="flat_fee">Flat Fee</SelectItem>
                    <SelectItem value="contingency">Contingency</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="pro_bono">Pro Bono</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newCase.fee_type === 'hourly' && (
                <div className="space-y-2">
                  <Label htmlFor="hourly-rate">Hourly Rate ($)</Label>
                  <Input
                    id="hourly-rate"
                    type="number"
                    placeholder="250"
                    value={newCase.hourly_rate}
                    onChange={(e) => setNewCase({ ...newCase, hourly_rate: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the case..."
                rows={3}
                value={newCase.description}
                onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createCase} disabled={creating} className="bg-navy hover:bg-navy-light">
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Case
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Matter Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {editingTemplate ? 'Edit Template' : 'Create Matter Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update template settings' : 'Create a reusable template for case creation'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Personal Injury - Auto Accident"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-case-type">Case Type *</Label>
              <Select
                value={templateForm.case_type}
                onValueChange={(value) => setTemplateForm({ ...templateForm, case_type: value })}
              >
                <SelectTrigger data-testid="template-case-type-select">
                  <SelectValue placeholder="Select case type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal_injury">Personal Injury</SelectItem>
                  <SelectItem value="family_law">Family Law</SelectItem>
                  <SelectItem value="criminal_defense">Criminal Defense</SelectItem>
                  <SelectItem value="business_litigation">Business Litigation</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="estate_planning">Estate Planning</SelectItem>
                  <SelectItem value="employment">Employment</SelectItem>
                  <SelectItem value="immigration">Immigration</SelectItem>
                  <SelectItem value="bankruptcy">Bankruptcy</SelectItem>
                  <SelectItem value="intellectual_property">Intellectual Property</SelectItem>
                  <SelectItem value="medical_malpractice">Medical Malpractice</SelectItem>
                  <SelectItem value="workers_compensation">Workers Compensation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template-fee-type">Default Fee Type</Label>
                <Select
                  value={templateForm.default_fee_type}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, default_fee_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="flat_fee">Flat Fee</SelectItem>
                    <SelectItem value="contingency">Contingency</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="pro_bono">Pro Bono</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {templateForm.default_fee_type === 'hourly' && (
                <div className="space-y-2">
                  <Label htmlFor="template-rate">Default Hourly Rate ($)</Label>
                  <Input
                    id="template-rate"
                    type="number"
                    placeholder="250"
                    value={templateForm.default_hourly_rate}
                    onChange={(e) => setTemplateForm({ ...templateForm, default_hourly_rate: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                placeholder="Brief description of this template..."
                rows={2}
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-1">Custom Fields</p>
              <p>Custom fields can be added after creating the template by editing a case created with this template.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} disabled={savingTemplate} className="bg-navy hover:bg-navy-light" data-testid="save-template-btn">
              {savingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingTemplate ? 'Update Template' : 'Create Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </PullToRefresh>
  );
}
